import "@/App.css";
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Quote, ArrowRight, ExternalLink, CalendarDays } from "lucide-react";
import { SmoothScroll } from "@/components/SmoothScroll";
import { ScheduleProvider, useSchedule } from "@/components/ScheduleModal";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { Stars, GoogleTag, ReviewCard } from "@/components/Reviews";
import { REVIEWS, FEATURED_REVIEW, GRID_REVIEWS, GOOGLE_REVIEWS_URL, GOOGLE_RATING } from "@/data/reviews";
import { businessEntity } from "@/data/business";

const SITE = "https://johncampbellinsurance.com";

const jsonLd = [
  {
    ...businessEntity,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: GOOGLE_RATING,
      reviewCount: REVIEWS.length,
      bestRating: "5",
      worstRating: "1",
    },
    review: REVIEWS.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
      reviewBody: r.text,
      publisher: { "@type": "Organization", name: "Google" },
    })),
  },
];

const ReadAllButton = ({ testid, variant = "outline" }) => (
  <a
    href={GOOGLE_REVIEWS_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full transition-colors duration-400 ${
      variant === "solid"
        ? "bg-[#f4f0ea] text-[#030303] hover:bg-[#d4af37]"
        : "border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#030303]"
    }`}
  >
    Read All Google Reviews
    <ExternalLink size={14} className="transition-transform duration-400 group-hover:translate-x-0.5" />
  </a>
);

function Hero() {
  return (
    <section data-testid="ce-hero" className="relative px-6 lg:px-10 max-w-[1400px] mx-auto pt-40 pb-16 lg:pb-24">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 1 }}
        className="flex items-center gap-3 mb-8"
      >
        <span className="h-px w-10 bg-[#d4af37]" />
        <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Client Experiences</span>
      </motion.div>

      <Reveal>
        <h1 className="serif font-light leading-[0.98] text-[#f4f0ea] text-[11vw] sm:text-[8.5vw] lg:text-[5.4vw] max-w-5xl">
          Trusted by the families we're <span className="italic text-[#d4af37]">honored to serve</span>.
        </h1>
      </Reveal>

      <Reveal delay={0.12} className="mt-8 max-w-2xl">
        <p className="text-[#a3a3a3] text-base lg:text-lg leading-relaxed tracking-wide">
          Real experiences from clients who trusted John Campbell Insurance to help protect what matters most.
        </p>
      </Reveal>

      <Reveal delay={0.22}>
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex items-center gap-3">
            <Stars size={18} />
            <span className="mono text-[11px] uppercase tracking-[0.3em] text-[#f4f0ea]">{GOOGLE_RATING} Google Rating</span>
          </div>
          <span className="hidden sm:block h-5 w-px bg-white/15" />
          <ReadAllButton testid="ce-hero-read-all" />
        </div>
      </Reveal>
    </section>
  );
}

function FeaturedReview() {
  const [expanded, setExpanded] = useState(false);
  const r = FEATURED_REVIEW;
  const LIMIT = 320;
  const isLong = r.text.length > LIMIT;
  const shown = isLong && !expanded ? `${r.text.slice(0, LIMIT).trimEnd()}…` : r.text;

  return (
    <section data-testid="ce-featured" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-10">
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Featured Experience</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <Reveal>
          <figure className="relative bg-gradient-to-b from-[#141414] to-[#0a0a0a] border border-[#d4af37]/25 rounded-sm px-8 py-12 lg:px-20 lg:py-20 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-[#d4af37]/45" />
            <Quote size={64} className="text-[#d4af37]/25 mb-6" />
            <div className="mb-8"><Stars size={18} /></div>
            <blockquote
              data-testid="ce-featured-text"
              className="serif font-light text-2xl sm:text-3xl lg:text-[2.4rem] leading-[1.35] text-[#f4f0ea] text-balance max-w-4xl"
            >
              “{shown}”
            </blockquote>
            {isLong && (
              <button
                onClick={() => setExpanded((v) => !v)}
                data-testid="ce-featured-toggle"
                className="mono text-[10px] uppercase tracking-[0.2em] text-[#d4af37] hover:text-[#f0d57d] mt-6 transition-colors duration-300"
              >
                {expanded ? "Read less" : "Read more"}
              </button>
            )}
            <figcaption className="mt-10 pt-8 border-t border-white/10 flex flex-wrap items-center gap-x-5 gap-y-2">
              <span className="serif text-xl text-[#f4f0ea]">{r.name}</span>
              <span className="h-4 w-px bg-white/15 hidden sm:block" />
              <GoogleTag />
            </figcaption>
          </figure>
        </Reveal>
      </div>
    </section>
  );
}

function ReviewGrid() {
  return (
    <section data-testid="ce-grid" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-12">
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">In Their Words</span>
          <span className="h-px flex-1 bg-white/10" />
          <span className="hidden sm:flex items-center gap-2 mono text-[10px] uppercase tracking-[0.3em] text-[#737373]">
            <Stars size={12} /> {GOOGLE_RATING}
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {GRID_REVIEWS.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.07}>
              <ReviewCard review={r} expandable testid={`ce-review-${r.id}`} />
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 flex justify-center">
            <ReadAllButton testid="ce-grid-read-all" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Relationships() {
  return (
    <section data-testid="ce-relationships" className="relative bg-[#030303] py-20 lg:py-28 border-t border-white/10">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
        <Reveal>
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37] block mb-6">Our Philosophy</span>
          <h2 className="serif font-light leading-[1.05] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
            Relationships before <span className="italic text-[#d4af37]">transactions</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-10 space-y-6 text-[#a3a3a3] text-lg leading-relaxed tracking-wide max-w-2xl mx-auto">
            <p>
              Every recommendation begins with listening, understanding your goals, and helping you make an informed
              decision without pressure.
            </p>
            <p className="text-[#f4f0ea]">
              We're grateful for every family that trusts John Campbell Insurance to help protect what matters most.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  const { openSchedule } = useSchedule();
  return (
    <section data-testid="ce-cta" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
        <Reveal>
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37] block mb-4">Let's talk</span>
          <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl max-w-2xl">
            Ready for a conversation centered on <span className="italic text-[#d4af37]">your needs</span>?
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="flex flex-wrap items-center gap-5">
          <button
            onClick={() => openSchedule()}
            data-testid="ce-schedule-btn"
            className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] rounded-full hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400"
          >
            <CalendarDays size={15} /> Schedule a Consultation
          </button>
          <Link
            to="/#quote"
            data-testid="ce-quote-btn"
            className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#f4f0ea] text-[#030303] rounded-full hover:bg-[#d4af37] transition-colors duration-400"
          >
            Get a Quote
            <ArrowRight size={15} className="transition-transform duration-400 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function ClientExperiences() {
  return (
    <div className="App grain bg-[#030303]">
      <Seo
        title="Client Experiences — John Campbell Insurance"
        description="Real experiences from families who trusted John Campbell Insurance to help protect what matters most. Read verified 5-star Google reviews."
        path="/client-experiences"
        jsonLd={jsonLd}
      />
      <ScheduleProvider>
        <SmoothScroll>
          <Navbar />
          <main>
            <Hero />
            <FeaturedReview />
            <ReviewGrid />
            <Relationships />
            <FinalCta />
          </main>
          <Footer />
        </SmoothScroll>
      </ScheduleProvider>
    </div>
  );
}
